import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import { Avatar } from '../Avatar';
import { tagColor } from './TagInput';
// Chat-bubble pin that grows on hover. At rest: a small charcoal circle (32×32)
// with the avatar inside and a small triangular nub below pointing at the
// world anchor. On hover the body expands BOTH wider (to make room for text)
// and slightly taller (so the two-line text fits without cramming). The
// avatar slides down a few pixels during the expansion to stay vertically
// centred in the bigger pill — same element, same colour, same font, just a
// few pixels of position shift as part of the same animation.
const BODY_W_RESTING = 32;
// Hover width is content-aware (measured at runtime). These caps keep
// short pills feeling deliberate and long pills from running off-screen.
const BODY_W_HOVERED_MIN = 200;
const BODY_W_HOVERED_MAX = 360;
const BODY_W_HOVERED_FALLBACK = 280;
const BODY_H_RESTING = 32;
const BODY_H_HOVERED = 40;
const BODY_RADIUS_RESTING = BODY_H_RESTING / 2; // 16 — circle at rest
const BODY_RADIUS_HOVERED = BODY_H_HOVERED / 2; // 20 — pill end-caps on hover
const AVATAR_SIZE = 28;
const AVATAR_INSET_X = (BODY_W_RESTING - AVATAR_SIZE) / 2; // 2 — same in both states
const AVATAR_TOP_RESTING = (BODY_H_RESTING - AVATAR_SIZE) / 2; // 2
const AVATAR_TOP_HOVERED = (BODY_H_HOVERED - AVATAR_SIZE) / 2; // 6
const NUB_W = 8;
const NUB_H = 4;
const NUB_X = (BODY_W_RESTING - NUB_W) / 2; // 12 — centred under the resting body
const NUB_Y = BODY_H_RESTING; // 32 — just below resting body
// World-anchor target inside the container — the nub's bottom-centre tip.
// Same point in container coords across both states so the avatar's screen
// position doesn't jump when the body expands. (The hover body absorbs the
// nub area into its own bounds; the nub remains drawn at the same coords
// but is invisible inside the same-coloured body.)
const NUB_TIP_X = NUB_X + NUB_W / 2; // 16
const NUB_TIP_Y = NUB_Y + NUB_H; // 36
// Container height covers the tallest state (hover body extends 4px below
// the nub tip). The resting state has empty space below the nub inside the
// container, but it's only as wide as the resting body (32) so the cursor
// won't trigger hover from off the visible pin.
const CONTAINER_H = Math.max(NUB_TIP_Y, BODY_H_HOVERED); // 40
// Shadows — restrained at rest, lifted on hover to reinforce the "rising
// forward" feel during expansion.
const SHADOW_RESTING = '0 2px 6px rgba(0, 0, 0, 0.35)';
const SHADOW_HOVERED = '0 6px 18px rgba(0, 0, 0, 0.5)';
// Pill text region: starts past the avatar's right edge with a comfortable
// gap, vertically centred in the body.
const PILL_TEXT_PADDING_LEFT = AVATAR_INSET_X + AVATAR_SIZE + 10; // 40
const PILL_TEXT_PADDING_RIGHT = 18;
// Animation duration for all transitioning properties (width, height,
// border-radius, avatar top). Single value so they stay in sync.
const TRANSITION = '150ms ease';
const formatRelativeTime = (timestamp) => {
    const now = Date.now() / 1000;
    const diff = now - timestamp;
    if (diff < 60)
        return 'just now';
    if (diff < 3600)
        return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400)
        return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800)
        return `${Math.floor(diff / 86400)}d ago`;
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
};
const authorDisplayName = (c) => {
    var _a, _b;
    const a = c.author;
    if (!a)
        return 'Unknown';
    const full = `${(_a = a.firstName) !== null && _a !== void 0 ? _a : ''} ${(_b = a.lastName) !== null && _b !== void 0 ? _b : ''}`.trim();
    return full || a.email || 'Unknown';
};
const ringFromComment = (c) => {
    var _a;
    const first = (_a = c.tags) === null || _a === void 0 ? void 0 : _a[0];
    return first ? tagColor(first).bg : null;
};
// HTML-overlay comment pin. Avatar element stays mounted across resting and
// hover states (same size, same colour, same font — no engine handoff).
// Per-frame screen position is updated via direct DOM mutation so camera
// motion doesn't cause React re-renders.
export const CommentHtmlPin = ({ viewer, comment, onActivate, }) => {
    var _a, _b, _c, _d;
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [hoverWidth, setHoverWidth] = useState(BODY_W_HOVERED_FALLBACK);
    const anchor = comment.anchor;
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        // Anchor lat/long are fixed; the height is resolved per-frame against the
        // terrain that's currently displayed so the pin hugs the surface instead
        // of staying frozen at the height it was dropped at.
        const carto = Cesium.Cartographic.fromDegrees(anchor.longitude, anchor.latitude);
        const world = new Cesium.Cartesian3();
        const update = () => {
            if (!containerRef.current || viewer.isDestroyed())
                return;
            // Clamp the pin to the active surface: sample the current terrain's
            // height at the anchor so the pin rides up and down when 3D terrain is
            // toggled. getHeight returns undefined when the covering tile hasn't
            // streamed in yet — fall back to the stored anchor height so the pin
            // never disappears mid-load.
            const surfaceHeight = viewer.scene.globe.getHeight(carto);
            Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, surfaceHeight !== null && surfaceHeight !== void 0 ? surfaceHeight : anchor.height, viewer.scene.globe.ellipsoid, world);
            const win = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, world);
            if (!win) {
                containerRef.current.style.display = 'none';
                return;
            }
            containerRef.current.style.display = '';
            containerRef.current.style.transform =
                `translate3d(${win.x - NUB_TIP_X}px, ${win.y - NUB_TIP_Y}px, 0)`;
        };
        update();
        return viewer.scene.postRender.addEventListener(update);
    }, [viewer, anchor.latitude, anchor.longitude, anchor.height]);
    const ringColor = ringFromComment(comment);
    const name = authorDisplayName(comment);
    const when = formatRelativeTime(comment.createdAt);
    // Measure the natural text width once mounted (and whenever the displayed
    // text changes) so the hover pill hugs short comments and stretches —
    // up to the cap — for longer ones. Hidden measurement div mirrors the
    // visible text styles exactly. useLayoutEffect runs before paint, so the
    // first hover already gets a correct width.
    useLayoutEffect(() => {
        var _a, _b;
        if (!measureRef.current)
            return;
        const header = measureRef.current.querySelector('[data-m="h"]');
        const content = measureRef.current.querySelector('[data-m="c"]');
        const widest = Math.max((_a = header === null || header === void 0 ? void 0 : header.scrollWidth) !== null && _a !== void 0 ? _a : 0, (_b = content === null || content === void 0 ? void 0 : content.scrollWidth) !== null && _b !== void 0 ? _b : 0);
        const needed = widest + PILL_TEXT_PADDING_LEFT + PILL_TEXT_PADDING_RIGHT;
        setHoverWidth(Math.max(BODY_W_HOVERED_MIN, Math.min(BODY_W_HOVERED_MAX, needed)));
    }, [name, when, comment.content]);
    const bodyW = isHovered ? hoverWidth : BODY_W_RESTING;
    const bodyH = isHovered ? BODY_H_HOVERED : BODY_H_RESTING;
    const bodyRadius = isHovered ? BODY_RADIUS_HOVERED : BODY_RADIUS_RESTING;
    const avatarTop = isHovered ? AVATAR_TOP_HOVERED : AVATAR_TOP_RESTING;
    const bodyShadow = isHovered ? SHADOW_HOVERED : SHADOW_RESTING;
    return (_jsxs("div", { ref: containerRef, style: {
            position: 'fixed',
            left: 0,
            top: 0,
            width: bodyW,
            height: CONTAINER_H,
            willChange: 'transform',
            userSelect: 'none',
            cursor: 'pointer',
            // Sit below the control panel (z-50) so pins don't bleed over UI
            // chrome at the edges of the viewport. The thread popover that
            // opens on click is at a much higher z-index and still rises above.
            zIndex: 40,
            transition: `width ${TRANSITION}`,
        }, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), onClick: (e) => onActivate({ x: e.clientX, y: e.clientY }), children: [_jsx("div", { style: {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: bodyW,
                    height: bodyH,
                    backgroundColor: 'var(--charcoal)',
                    borderRadius: bodyRadius,
                    boxShadow: bodyShadow,
                    transition: `width ${TRANSITION}, height ${TRANSITION}, border-radius ${TRANSITION}, box-shadow ${TRANSITION}`,
                } }), _jsx("div", { style: {
                    position: 'absolute',
                    left: NUB_X,
                    top: NUB_Y,
                    width: 0,
                    height: 0,
                    borderLeft: `${NUB_W / 2}px solid transparent`,
                    borderRight: `${NUB_W / 2}px solid transparent`,
                    borderTop: `${NUB_H}px solid var(--charcoal)`,
                } }), isHovered && (_jsxs("div", { style: {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: hoverWidth,
                    height: BODY_H_HOVERED,
                    padding: `0 ${PILL_TEXT_PADDING_RIGHT}px 0 ${PILL_TEXT_PADDING_LEFT}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    // Don't intercept pointer events — let them reach the container
                    // so onMouseLeave fires on the container reliably.
                    pointerEvents: 'none',
                }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }, children: [_jsx("span", { className: "truncate", style: { color: 'var(--sand)', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }, children: name }), _jsx("span", { style: { color: 'var(--dew)', fontSize: 11, opacity: 0.7, flexShrink: 0, lineHeight: 1.2 }, children: when })] }), _jsx("span", { className: "truncate", style: { color: 'var(--sand)', fontSize: 12, opacity: 0.85, marginTop: 4, lineHeight: 1.2 }, children: comment.content })] })), _jsxs("div", { ref: measureRef, "aria-hidden": true, style: {
                    position: 'absolute',
                    top: 0,
                    left: -9999,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                }, children: [_jsxs("div", { "data-m": "h", style: { display: 'inline-flex', alignItems: 'baseline', gap: 8 }, children: [_jsx("span", { style: { fontSize: 13, fontWeight: 600 }, children: name }), _jsx("span", { style: { fontSize: 11 }, children: when })] }), _jsx("div", { "data-m": "c", style: { fontSize: 12 }, children: comment.content })] }), _jsx(Avatar, { userId: (_a = comment.author) === null || _a === void 0 ? void 0 : _a.userId, commentColor: (_b = comment.author) === null || _b === void 0 ? void 0 : _b.commentColor, photoUrl: (_c = comment.author) === null || _c === void 0 ? void 0 : _c.avatarUrl, source: (_d = comment.author) !== null && _d !== void 0 ? _d : {}, size: AVATAR_SIZE, border: ringColor ? `2px solid ${ringColor}` : undefined, style: {
                    position: 'absolute',
                    left: AVATAR_INSET_X,
                    top: avatarTop,
                    fontSize: 13,
                    fontWeight: 'bold',
                    transition: `top ${TRANSITION}`,
                } })] }));
};
